import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';

interface InfoButtonProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

const InfoButton: React.FC<InfoButtonProps> = ({
  title = 'About this card',
  children,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const focusWithoutScroll = (el: HTMLElement | null | undefined) => {
    if (!el) return;
    try {
      (el as any).focus?.({ preventScroll: true });
    } catch {
      el.focus();
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('.react-resizable-handle')) {
        return; // ignore resize handle interactions
      }
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        focusWithoutScroll(buttonRef.current || undefined);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Find the closest card container to center the modal within
  const findCardContainerRect = (): DOMRect | null => {
    let el: HTMLElement | null = containerRef.current as HTMLElement | null;
    while (el) {
      if (el.hasAttribute('data-card-container')) {
        return el.getBoundingClientRect();
      }
      el = el.parentElement;
    }
    return null;
  };

  useEffect(() => {
    if (!open) return;
    const updateRect = () => setAnchorRect(findCardContainerRect());
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  const overlayStyle = useMemo<React.CSSProperties>(() => {
    if (!anchorRect) return { display: 'none' };
    return {
      position: 'fixed',
      top: anchorRect.top,
      left: anchorRect.left,
      width: anchorRect.width,
      height: anchorRect.height,
      zIndex: 99999,
    } as React.CSSProperties;
  }, [anchorRect]);

  return (
    <div ref={containerRef} className={`relative inline-block ${className || ''}`}>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center p-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
        aria-label="Show card information"
        title="How this card works"
      >
        <Info className="h-4 w-4" />
      </button>

      {open &&
        anchorRect &&
        createPortal(
          <div style={overlayStyle} className="pointer-events-auto">
            {/* Backdrop inside card area */}
            <div
              className="absolute inset-0 bg-black/10 dark:bg-black/30 rounded-xl"
              onClick={() => setOpen(false)}
            />
            {/* Centered modal */}
            <div className="absolute inset-0 flex items-center justify-center p-3">
              <div className="w-96 max-w-[90%] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl p-4 relative">
                <button
                  onClick={() => setOpen(false)}
                  className="absolute top-2 right-2 p-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {title}
                </div>
                <div className="text-xs leading-relaxed text-gray-600 dark:text-gray-300 space-y-2">
                  {children}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default InfoButton;
